import { UzCombobox } from '@utazzo/ui';

import { usePickerOptions } from './usePickerOptions';

import * as suppliersApi from '@/features/suppliers/suppliers.api';

interface SupplierPickerProps {
  label?: string;
  value: string | null;
  onValueChange: (value: string | null) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  hint?: string;
  /** Saved supplier name to show in the field until one is picked. */
  currentName?: string | null;
}

export const SupplierPicker = ({
  label = 'Vendor / Supplier',
  value,
  onValueChange,
  required,
  disabled,
  error,
  hint,
  currentName,
}: SupplierPickerProps): React.JSX.Element => {
  const { options, loading, setSearch } = usePickerOptions(
    {
      cacheKey: 'picker.suppliers',
      fetch: async (search) => {
        const res = await suppliersApi.listSuppliers({
          page: 1,
          pageSize: 30,
          sortDir: 'asc',
          ...(search ? { search } : {}),
        });
        return res.data;
      },
      fetchOne: async (id) => {
        try {
          return await suppliersApi.getSupplier(id);
        } catch {
          return null;
        }
      },
      toOption: (s) => ({
        value: s.id,
        label: s.name,
        description: s.code ?? undefined,
      }),
    },
    value,
  );

  return (
    <UzCombobox
      label={label}
      placeholder="Select a supplier"
      fallbackLabel={currentName || undefined}
      searchPlaceholder="Search suppliers..."
      value={value ?? ''}
      onValueChange={(v) => onValueChange(v || null)}
      onSearchChange={setSearch}
      options={options}
      loading={loading}
      required={required}
      disabled={disabled}
      error={error}
      hint={hint}
    />
  );
};
